/**
 * PayDeal — private payment page for a local business deal.
 * Route: /pay/deal/:token   (link emailed + texted to the business; expires after 7 days)
 *
 * Every call is a POST to /general/getgeneral (same as GuarantorReview), with only
 * the Content-Type header. Types: dl open link · dv send code · dvc check code ·
 * dd deal details · dc pay · dps payment status. Response keys are short (see
 * backend/routes/general.py).
 *
 * 1. Verify: business enters the phone or email we have on file → we send a 6-digit code.
 * 2. Code:   they enter it → server returns a session `dsn` (kept on this device until it expires).
 * 3. Review: flyer exactly as customers see it, run dates, areas, price, advertising rules.
 *    "Pay $X" → Stripe-hosted Checkout (card, Apple Pay, Google Pay). Returns with ?paid=1.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { DEAL_PAGE_STATE, DEAL_PAID_RETURN_PARAM, DEAL_VERIFY_CHANNEL } from '../config/constants';

const SESSION_KEY = (token) => `deal_session_${token}`;

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) : '';

const readSession = (token) => {
  try {
    const raw = localStorage.getItem(SESSION_KEY(token));
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s.expires || new Date(s.expires) <= new Date()) {
      localStorage.removeItem(SESSION_KEY(token));
      return null;
    }
    return s.value;
  } catch {
    return null;
  }
};
const saveSession = (token, value, expires) => {
  try { localStorage.setItem(SESSION_KEY(token), JSON.stringify({ value, expires })); } catch { /* private mode */ }
};
const clearSession = (token) => {
  try { localStorage.removeItem(SESSION_KEY(token)); } catch { /* ignore */ }
};

// POST /general/getgeneral → { success, message, data }
async function getGeneral(body) {
  let res;
  try {
    res = await fetch(`${API_BASE_URL}/general/getgeneral`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    return { success: false, message: "We couldn't reach the server. Please check your connection and try again." };
  }
  if (res.status === 429) return { success: false, message: 'Too many tries. Please wait a minute and try again.' };
  const result = await res.json().catch(() => null);
  return result || { success: false, message: 'Something went wrong. Please try again.' };
}

const Shell = ({ subtitle, children }) => (
  <div className="min-h-screen bg-gray-50 flex items-start justify-center px-4 py-8 sm:py-12">
    <div className="w-full max-w-md">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 font-sans">Rent<span className="text-emerald-600">AI</span></h1>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
      {children}
    </div>
  </div>
);

const Card = ({ children }) => <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">{children}</div>;

const Steps = ({ step }) => (
  <div className="flex items-center gap-2 mb-6 px-4" aria-hidden="true">
    {[1, 2, 3].map((s, i, arr) => (
      <React.Fragment key={s}>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
          step >= s ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-500'}`}>{s}</div>
        {i < arr.length - 1 && <div className={`flex-1 h-0.5 ${step > s ? 'bg-emerald-300' : 'bg-gray-200'}`} />}
      </React.Fragment>
    ))}
  </div>
);

const ErrorText = ({ children }) =>
  children ? <p role="alert" className="text-sm text-red-600 mt-3">{children}</p> : null;

const Flyer = ({ d }) => (
  <div className="rounded-2xl overflow-hidden bg-[#0d0d0d] text-white">
    <div className="h-44 bg-[#262626] flex items-center justify-center">
      {d.bi
        ? <img src={d.bi} alt="" className="w-full h-full object-cover" />
        : <span className="text-xs text-gray-400">No banner</span>}
    </div>
    <div className="p-4 space-y-2">
      <div className="flex items-center gap-2">
        {d.bl
          ? <img src={d.bl} alt="" className="w-6 h-6 rounded-full object-cover" />
          : <div className="w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold flex items-center justify-center">{(d.bn || '?').charAt(0)}</div>}
        <span className="text-xs text-gray-300">{d.bn}</span>
      </div>
      <p className="font-bold text-lg leading-snug">{d.hl}</p>
      {d.bd && <p className="text-sm text-gray-300 leading-relaxed">{d.bd}</p>}
      {d.pc && (
        <span className="inline-block px-2.5 py-1 rounded-lg border border-dashed border-emerald-400/60 text-emerald-300 text-xs font-semibold">
          Code: {d.pc}
        </span>
      )}
    </div>
  </div>
);

const PayDeal = () => {
  const { token } = useParams();
  const [search] = useSearchParams();
  const returnedFromStripe = search.get(DEAL_PAID_RETURN_PARAM) === '1';

  const [info, setInfo] = useState(null);        // dl
  const [step, setStep] = useState(1);           // 1 verify, 2 code, 3 review
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fatal, setFatal] = useState('');

  const [countries, setCountries] = useState([{ country_id: 'us', name: 'United States', phone_code: '+1' }]);
  const [channel, setChannel] = useState(DEAL_VERIFY_CHANNEL.PHONE);
  const [dcc, setDcc] = useState('+1');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(null);        // dv data: {sv, mk, dcd?}
  const [code, setCode] = useState('');

  const [details, setDetails] = useState(null);  // dd data
  const [agreed, setAgreed] = useState(false);
  const [paidInfo, setPaidInfo] = useState(null);

  // Country codes — same source as GuarantorReview
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/business-main/countries`);
        const data = await res.json();
        if (data.countries?.length > 0) setCountries(data.countries);
      } catch { /* keep +1 */ }
    })();
  }, []);

  const loadDetails = useCallback(async () => {
    const dsn = readSession(token);
    if (!dsn) return false;
    const result = await getGeneral({ type: 'dd', dt: token, dsn });
    if (result.success) {
      setDetails(result.data);
      setStep(3);
      return true;
    }
    if (result.data?.step === 'verify') { clearSession(token); setStep(1); return false; }
    setError(result.message || 'Something went wrong. Please try again.');
    return false;
  }, [token]);

  // Initial load
  useEffect(() => {
    (async () => {
      const result = await getGeneral({ type: 'dl', dt: token });
      if (!result.success) {
        setFatal(result.message === 'Invalid or expired link'
          ? "This link isn't valid. Please check the link in your email or text."
          : result.message);
        return;
      }
      const i = result.data;
      setInfo(i);
      if (i.cc) setDcc(i.cc);
      if (!i.hp && i.he) setChannel(DEAL_VERIFY_CHANNEL.EMAIL);
      if (i.st === DEAL_PAGE_STATE.OPEN && !returnedFromStripe) await loadDetails();
    })();
  }, [token, returnedFromStripe, loadDetails]);

  // After Stripe: wait for the webhook to mark it paid
  useEffect(() => {
    if (!returnedFromStripe) return undefined;
    let tries = 0;
    let stopped = false;
    const poll = async () => {
      const result = await getGeneral({ type: 'dps', dt: token });
      if (result.success && result.data?.st === DEAL_PAGE_STATE.PAID) { setPaidInfo(result.data); return; }
      tries += 1;
      if (!stopped && tries < 15) setTimeout(poll, 2000);
      else if (!stopped) setPaidInfo({ pending: true });
    };
    poll();
    return () => { stopped = true; };
  }, [returnedFromStripe, token]);

  const sendCode = async (e) => {
    e?.preventDefault();
    setError('');
    const body = channel === DEAL_VERIFY_CHANNEL.PHONE
      ? { type: 'dv', dt: token, dp: phone.trim(), dcc }
      : { type: 'dv', dt: token, de: email.trim() };
    if (!body.dp && !body.de) { setError(channel === DEAL_VERIFY_CHANNEL.PHONE ? 'Enter your phone number.' : 'Enter your email.'); return; }
    setLoading(true);
    const result = await getGeneral(body);
    setLoading(false);
    if (result.success && result.data?.step === 'verify_code') {
      setSent(result.data);
      setCode('');
      setStep(2);
    } else {
      setError(result.message || 'Verification failed');
    }
  };

  const checkCode = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(code)) { setError('Enter the 6-digit code.'); return; }
    setLoading(true);
    const result = await getGeneral({ type: 'dvc', dt: token, dcode: code });
    if (result.success) {
      saveSession(token, result.data.dsn, result.data.sx);
      await loadDetails();
    } else {
      if (result.data?.step === 'verify') setStep(1);
      setError(result.message || 'Verification failed');
    }
    setLoading(false);
  };

  const pay = async () => {
    setError('');
    if (!agreed) { setError('Please accept the advertising rules.'); return; }
    setLoading(true);
    const result = await getGeneral({ type: 'dc', dt: token, dsn: readSession(token) || '', dat: true });
    if (result.success && result.data?.cu) {
      window.location.assign(result.data.cu);
      return;
    }
    if (result.data?.step === 'verify') { clearSession(token); setStep(1); }
    setError(result.message || 'Something went wrong. Please try again.');
    setLoading(false);
  };

  // ── Terminal states ──
  if (fatal) return <Shell subtitle="Deal payment"><Card><p className="text-gray-700">{fatal}</p></Card></Shell>;
  if (!info) {
    return <Shell subtitle="Deal payment"><Card><p className="text-sm text-gray-500">Loading…</p></Card></Shell>;
  }
  if (returnedFromStripe || info.st === DEAL_PAGE_STATE.PAID) {
    const pending = returnedFromStripe && (!paidInfo || paidInfo.pending);
    return (
      <Shell subtitle={info.bn}>
        <Card>
          <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center text-2xl mb-3" aria-hidden="true">✓</div>
          {pending && !paidInfo?.pending ? (
            <>
              <h2 className="text-lg font-semibold text-gray-900">Confirming your payment…</h2>
              <p className="text-sm text-gray-500 mt-1">This usually takes a few seconds.</p>
            </>
          ) : paidInfo?.pending ? (
            <>
              <h2 className="text-lg font-semibold text-gray-900">Payment submitted</h2>
              <p className="text-sm text-gray-500 mt-1">We're still confirming it with Stripe. You'll get an email as soon as it's done — you can close this page.</p>
            </>
          ) : (
            <>
              <h2 className="text-lg font-semibold text-gray-900">Payment received</h2>
              {paidInfo?.ls ? (
                <p className="text-sm text-gray-600 mt-1">Your deal runs <strong>{fmtDate(paidInfo.ls)} – {fmtDate(paidInfo.le)}</strong>.</p>
              ) : (
                <p className="text-sm text-gray-600 mt-1">This deal is already paid.</p>
              )}
              <p className="text-sm text-gray-500 mt-2">Stripe emails your receipt. Thanks for advertising with {info.br}!</p>
            </>
          )}
        </Card>
      </Shell>
    );
  }
  if (info.st !== DEAL_PAGE_STATE.OPEN) {
    const msg = {
      [DEAL_PAGE_STATE.EXPIRED]: 'This payment link has expired. Reply to our email or contact us and we\'ll send a new one.',
      [DEAL_PAGE_STATE.CANCELLED]: 'This payment link was cancelled. Please contact us for a new one.',
      [DEAL_PAGE_STATE.REFUNDED]: 'This deal was refunded.',
    }[info.st] || 'This link is no longer active.';
    return <Shell subtitle={info.bn}><Card><p className="text-gray-700">{msg}</p></Card></Shell>;
  }

  // ── Steps ──
  return (
    <Shell subtitle={`Deal payment · ${info.bn}`}>
      <Steps step={step} />

      {step === 1 && (
        <form onSubmit={sendCode} className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900">Verify it's you</h2>
          <p className="text-sm text-gray-500 mt-1 mb-5">
            Confirm the {info.hp && info.he ? 'phone or email' : info.hp ? 'phone' : 'email'} we have
            for {info.bn}. We'll send you a 6-digit code.
          </p>

          {info.hp && info.he && (
            <div className="grid grid-cols-2 gap-2 mb-4" role="tablist">
              {[DEAL_VERIFY_CHANNEL.PHONE, DEAL_VERIFY_CHANNEL.EMAIL].map((c) => (
                <button key={c} type="button" role="tab" aria-selected={channel === c} onClick={() => { setChannel(c); setError(''); }}
                  className={`py-2 rounded-lg text-sm font-medium border ${channel === c ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-600'}`}>
                  {c === DEAL_VERIFY_CHANNEL.PHONE ? 'Text me' : 'Email me'}
                </button>
              ))}
            </div>
          )}

          {channel === DEAL_VERIFY_CHANNEL.PHONE ? (
            <div className="grid grid-cols-[85px_1fr] gap-2">
              <div>
                <label htmlFor="pd-cc" className="block text-sm text-gray-600 mb-1">Code</label>
                <select id="pd-cc" value={dcc} onChange={(e) => setDcc(e.target.value)}
                  className="w-full px-2 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 bg-white">
                  {[...new Set([dcc, ...countries.map((c) => c.phone_code)])].map((pc) => <option key={pc} value={pc}>{pc}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="pd-phone" className="block text-sm text-gray-600 mb-1">Mobile number <span className="text-gray-400">({info.hp})</span></label>
                <input id="pd-phone" type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="(404) 555-0142"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400" />
              </div>
            </div>
          ) : (
            <div>
              <label htmlFor="pd-email" className="block text-sm text-gray-600 mb-1">Email <span className="text-gray-400">({info.he})</span></label>
              <input id="pd-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@business.com"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400" />
            </div>
          )}
          <ErrorText>{error}</ErrorText>
          <button type="submit" disabled={loading}
            className="mt-5 w-full py-3 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-60">
            {loading ? 'Sending…' : 'Send code'}
          </button>
          <p className="text-xs text-gray-400 mt-4 text-center">Can't verify? Reply to the email we sent and we'll update your details.</p>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={checkCode} className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900">Enter the code</h2>
          <p className="text-sm text-gray-500 mt-1 mb-5">
            We sent a 6-digit code {sent?.sv === DEAL_VERIFY_CHANNEL.PHONE ? 'by text to your phone' : 'to your email'} {sent?.mk}.
          </p>
          <label htmlFor="pd-code" className="sr-only">6-digit code</label>
          <input id="pd-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="••••••"
            className="w-full px-3 py-3 border border-gray-200 rounded-lg text-2xl tracking-[0.5em] text-center focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400" />
          {sent?.dcd && <p className="text-xs text-gray-400 mt-2 text-center">Dev mode code: {sent.dcd}</p>}
          <ErrorText>{error}</ErrorText>
          <button type="submit" disabled={loading}
            className="mt-5 w-full py-3 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-60">
            {loading ? 'Checking…' : 'Verify'}
          </button>
          <div className="flex justify-between mt-4 text-sm">
            <button type="button" onClick={() => { setStep(1); setError(''); }} className="text-gray-500 hover:text-gray-700">Back</button>
            <button type="button" onClick={sendCode} disabled={loading} className="text-emerald-700 hover:underline">Resend code</button>
          </div>
        </form>
      )}

      {step === 3 && details && (
        <div className="space-y-4">
          <Card>
            <h2 className="text-lg font-semibold text-gray-900">Review your deal</h2>
            <p className="text-sm text-gray-500 mt-1 mb-4">This is exactly how customers will see it in the {info.br} app.</p>
            <Flyer d={details} />
            <dl className="mt-5 space-y-3 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Runs</dt>
                <dd className="text-gray-900 text-right">
                  {fmtDate(details.rs)} – {fmtDate(details.re)}
                  <span className="block text-xs text-gray-400">{details.dur} days{new Date(details.rs) > new Date() ? '' : ', starting when you pay'}</span>
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Shown to</dt>
                <dd className="text-gray-900 text-right">
                  {details.zc?.length > 0 && <span className="block">People in {details.zc.join(', ')}</span>}
                  {details.un?.length > 0 && <span className="block">Verified students at {details.un.join(', ')}</span>}
                </dd>
              </div>
              <div className="flex justify-between gap-4 pt-3 border-t border-gray-100">
                <dt className="text-gray-900 font-semibold">Total</dt>
                <dd className="text-gray-900 font-semibold">{money(details.amt)}</dd>
              </div>
            </dl>
          </Card>

          <Card>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">{details.th}</h3>
            <div className="max-h-40 overflow-y-auto text-xs text-gray-600 whitespace-pre-line border border-gray-100 rounded-lg p-3 bg-gray-50">
              {details.tc}
            </div>
            <label className="flex items-start gap-2 mt-4 text-sm text-gray-700">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 h-4 w-4 accent-emerald-600" />
              I agree to the advertising rules on behalf of {details.bn}.
            </label>
            <ErrorText>{error}</ErrorText>
            <button type="button" onClick={pay} disabled={loading || !agreed}
              className="mt-5 w-full py-3 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50">
              {loading ? 'Opening secure checkout…' : `Pay ${money(details.amt)}`}
            </button>
            <p className="text-xs text-gray-400 mt-3 text-center">Secure payment by Stripe · card, Apple Pay or Google Pay · no account needed</p>
          </Card>
          <p className="text-xs text-gray-400 text-center">Link expires {fmtDate(details.te)}</p>
        </div>
      )}
    </Shell>
  );
};

export default PayDeal;
